import { useSyncExternalStore } from 'react';

const STORAGE_KEY = 'dragonfly-map-preference-v1';
const listeners = new Set<() => void>();

function readPreference(): boolean | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'always';
  } catch {
    return null;
  }
}

const serverSnapshot = { alwaysLoad: false, revision: 0 };
let snapshot = { alwaysLoad: readPreference() ?? false, revision: 0 };

function publish(alwaysLoad: boolean) {
  snapshot = { alwaysLoad, revision: snapshot.revision + 1 };
  listeners.forEach((listener) => listener());
}

function onStorage(event: StorageEvent) {
  if (event.key === STORAGE_KEY || event.key === null) publish(readPreference() ?? false);
}

function subscribe(listener: () => void) {
  if (listeners.size === 0) {
    window.addEventListener('storage', onStorage);
    const stored = readPreference();
    if (stored !== null && stored !== snapshot.alwaysLoad) publish(stored);
  }
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) window.removeEventListener('storage', onStorage);
  };
}

function setAlwaysLoad(alwaysLoad: boolean) {
  try {
    window.localStorage.setItem(STORAGE_KEY, alwaysLoad ? 'always' : 'ask');
  } catch {
    // The choice still applies to this page when browser storage is unavailable.
  }
  publish(alwaysLoad);
}

export function useMapPreference() {
  const preference = useSyncExternalStore(
    subscribe,
    () => snapshot,
    () => serverSnapshot,
  );
  return { ...preference, setAlwaysLoad };
}
